import React from 'react';
import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { legalEntityClasses } from '../config/calendarTheme';

/**
 * Contact mention node data structure
 */
export interface ContactMentionData {
  contactId: string;
  displayName: string;
  contactType: 'client' | 'attorney' | 'opposing_counsel' | 'witness' | 'other';
  email?: string;
  phone?: string;
}

/**
 * Serialized contact mention node for JSON storage
 */
export interface SerializedContactMentionNode extends Spread<
  {
    contactData: ContactMentionData;
    type: 'contact-mention';
    version: 1;
  },
  SerializedLexicalNode
> {}

/**
 * Custom Lexical node for @mention contact references
 * Creates immutable "pill" elements that link to contact records
 */
export class ContactMentionNode extends DecoratorNode<React.JSX.Element> {
  __contactData: ContactMentionData;

  static getType(): string {
    return 'contact-mention';
  }

  static clone(node: ContactMentionNode): ContactMentionNode {
    return new ContactMentionNode(node.__contactData, node.__key);
  }

  constructor(contactData: ContactMentionData, key?: NodeKey) {
    super(key);
    this.__contactData = contactData;
  }

  // Serialization for saving to database
  exportJSON(): SerializedContactMentionNode {
    return {
      contactData: this.__contactData,
      type: 'contact-mention',
      version: 1,
    };
  }

  // Deserialization from database
  static importJSON(serializedNode: SerializedContactMentionNode): ContactMentionNode {
    const { contactData } = serializedNode;
    return $createContactMentionNode(contactData);
  }

  // Export to DOM (for copy/paste, print, etc.)
  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-contact-mention', 'true');
    element.setAttribute('data-contact-id', this.__contactData.contactId);
    element.className = legalEntityClasses.contact;
    element.textContent = `@${this.__contactData.displayName}`;
    return { element };
  }

  // Import from DOM (for paste operations)
  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-contact-mention')) {
          return null;
        }
        return {
          conversion: convertContactMentionElement,
          priority: 1,
        };
      },
    };
  }

  // React component for rendering
  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = legalEntityClasses.contact;
    return span;
  }

  updateDOM(): false {
    return false; // Immutable node - never update in place
  }

  // React component for the mention pill
  decorate(): React.JSX.Element {
    return (
      <ContactMentionComponent
        contactData={this.__contactData}
        nodeKey={this.getKey()}
      />
    );
  }

  // Getters for accessing data
  getContactData(): ContactMentionData {
    return this.getLatest().__contactData;
  }

  getContactId(): string {
    return this.getLatest().__contactData.contactId;
  }

  getDisplayName(): string {
    return this.getLatest().__contactData.displayName;
  }

  // Text representation for search/export
  getTextContent(): string {
    return `@${this.__contactData.displayName}`;
  }

  // Make node immutable to prevent editing
  isImmutable(): boolean {
    return true;
  }

  // Allow the node to be selected for deletion
  isKeyboardSelectable(): boolean {
    return true;
  }
}

/**
 * React component for rendering contact mention pills
 */
interface ContactMentionComponentProps {
  contactData: ContactMentionData;
  nodeKey: NodeKey;
}

function ContactMentionComponent({ contactData }: ContactMentionComponentProps): React.JSX.Element {
  const handleClick = () => {
    // Navigate to contact detail page
    // This will be integrated with React Router
    const contactUrl = `/contacts/${contactData.contactId}`;
    window.location.href = contactUrl; // Temporary - will use React Router navigation
  };

  const getContactIcon = () => {
    switch (contactData.contactType) {
      case 'client':
        return '👤';
      case 'attorney':
        return '⚖️';
      case 'opposing_counsel':
        return '🏛️';
      case 'witness':
        return '📋';
      default:
        return '📞';
    }
  };

  return (
    <span
      className={legalEntityClasses.contact}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      title={`Contact: ${contactData.displayName}${contactData.email ? ` (${contactData.email})` : ''}`}
      data-contact-id={contactData.contactId}
    >
      <span className="mr-1" aria-hidden="true">
        {getContactIcon()}
      </span>
      @{contactData.displayName}
    </span>
  );
}

/**
 * DOM conversion function for paste operations
 */
function convertContactMentionElement(domNode: HTMLElement): DOMConversionOutput {
  const contactId = domNode.getAttribute('data-contact-id');
  const displayName = domNode.textContent?.replace('@', '') || '';
  
  if (contactId && displayName) {
    const contactData: ContactMentionData = {
      contactId,
      displayName,
      contactType: 'other', // Default type, can be enhanced
    };
    
    const node = $createContactMentionNode(contactData);
    return { node };
  }
  
  return { node: null };
}

/**
 * Factory function for creating contact mention nodes
 */
export function $createContactMentionNode(contactData: ContactMentionData): ContactMentionNode {
  return new ContactMentionNode(contactData);
}

/**
 * Type guard for contact mention nodes
 */
export function $isContactMentionNode(node: LexicalNode | null | undefined): node is ContactMentionNode {
  return node instanceof ContactMentionNode;
} 